import React, {useCallback, useEffect, useRef, useState} from 'react';
import {View} from 'react-native';
import {observer} from 'mobx-react-lite';

import Icon from '../../components/Icon';
import BackButton from '../../components/rewards/BackButton';
import LmgDepartmentStatePicker from '../../components/rewards/LmgDepartmentStatePicker';
import RewardsProgramImage from '../../components/rewards/ProgramImage';
import RewardsCard from '../../components/rewards/RewardsCard';
import RewardsLink from '../../components/rewards/RewardsLink';
import Button from '../../components/themed/Button';
import KeyboardAwareScrollView from '../../components/themed/KeyboardAwareScrollView';
import Text from '../../components/themed/Text';
import ContentView from '../../components/ui-controls/layout/ContentView';
import {AppRoute} from '../../models/AppRoute';
import {RewardsSignUpTypeParams, RouteNavigatorScreenProps} from '../../models/navigationTypes';
import {navigate} from '../../navigation/RootNavigation';
import {useStores} from '../../stores/use-stores';
import {logWarning} from '../../utils/errorLogging';
import {classifyStaffConflict, isLmgStaffActivationBlocked} from '../../utils/staffActivation';

import {AccountStatus} from './types';

// R19/R3 shared canonical copy — the dash is a real em-dash (—), not a hyphen.
const INVALID_INVITE_COPY = 'Your invite link is invalid — request a new one.';

// Confirmed-selection summary palette. Mirrors rewardsTheme tokens
// (textMuted #6B7280, black #111928, borderSubtle #E5E7EB); this screen already
// inlines hex for state (see the error color below), so the summary stays self-contained.
const SUMMARY_MUTED = '#6B7280';
const SUMMARY_VALUE = '#111928';
const SUMMARY_DIVIDER = '#E5E7EB';

type SelectionSummaryRowProps = {
  icon: string;
  label: string;
  value: string;
  testID?: string;
};

// One labelled selection (icon + field label + chosen value). Replaces the two
// bare value lines so a staff member can tell department from state at a glance.
const SelectionSummaryRow = ({icon, label, value, testID}: SelectionSummaryRowProps) => (
  <View
    style={{flexDirection: 'row', alignItems: 'flex-start', gap: 10}}
    accessible
    accessibilityLabel={`${label}: ${value}`}
    testID={testID}>
    <Icon name={icon} size={16} color={SUMMARY_MUTED} style={{width: 20, marginTop: 2}} />
    <View style={{flex: 1}}>
      <Text variant="caption" style={{color: SUMMARY_MUTED}}>
        {label}
      </Text>
      <Text variant="body" style={{color: SUMMARY_VALUE, fontWeight: '600'}}>
        {value}
      </Text>
    </View>
  </View>
);

const LmgStaffSelectionScreen = observer(
  ({route, navigation}: RouteNavigatorScreenProps<AppRoute.REWARDS_LMG_STAFF_SELECTION>) => {
    const {rewardsStore} = useStores();
    const {flowType} = route.params || ({} as RewardsSignUpTypeParams);
    const [selectedDepartment, setSelectedDepartment] = useState<string | null>(null);
    const [selectedState, setSelectedState] = useState<string | null>(null);
    const [signUpError, setSignUpError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const mountedRef = useRef(true);

    useEffect(() => {
      mountedRef.current = true;
      return () => {
        mountedRef.current = false;
      };
    }, []);

    // R25: fetch the department/state option lists on mount; the picker owns the
    // loading/error/retry presentation, this screen just passes state through.
    useEffect(() => {
      rewardsStore.loadLmgStaffOptions();
    }, [rewardsStore]);

    const handleRetryOptions = useCallback(() => {
      rewardsStore.loadLmgStaffOptions();
    }, [rewardsStore]);

    // R10: submit the exact string returned by the API — no trim/case change.
    const handleSelectDepartment = useCallback(
      (value: string) => {
        setSelectedDepartment(value);
        rewardsStore.setLmgDepartment(value);
      },
      [rewardsStore],
    );

    const handleSelectState = useCallback(
      (value: string) => {
        setSelectedState(value);
        rewardsStore.setLmgState(value);
      },
      [rewardsStore],
    );

    // R13: return to the picker to correct the choice; clear local + store state.
    const handleChangeSelection = useCallback(() => {
      setSelectedDepartment(null);
      setSelectedState(null);
      rewardsStore.setLmgDepartment('');
      rewardsStore.setLmgState('');
    }, [rewardsStore]);

    const bothSelected = !!selectedDepartment && !!selectedState;

    const handleContinue = async () => {
      if (!bothSelected || isSubmitting) return;
      setSignUpError(null);
      setIsSubmitting(true);
      try {
        const result = await rewardsStore.signUpStaff(flowType || AccountStatus.NO_LITE_SIGNUP);
        if (!mountedRef.current) return;
        if (result.ok) {
          // R15/O-1: LMG activation skipped (Zen↔Zoho) — divert to Contact-Support,
          // never reach Success. Check before the success navigation.
          if (isLmgStaffActivationBlocked(result.data)) {
            navigate(AppRoute.REWARDS_STAFF_ACTIVATION_BLOCKED, {
              conflictKind: classifyStaffConflict(result.data.lmgStaffActivation?.reason),
            });
            return;
          }
          navigate(AppRoute.REWARDS_SUCCESS, {flowType});
          return;
        }
        switch (result.reason) {
          case 'invalid_lmg_code':
            // R19: 403 — invite code invalid; inline copy, no logout, no advance.
            setSignUpError(INVALID_INVITE_COPY);
            break;
          case 'session_expired':
            // R20: 410 — workflow TTL elapsed; restart phone verification. No logout.
            navigate(AppRoute.REWARDS_VERIFICATION, {
              flowType: flowType || AccountStatus.NO_LITE_SIGNUP,
              returnReason: 'verification-expired',
            });
            break;
          case 'account_not_found':
            // REQ-2: 422 — no account on file, terminal. Reuse the store-path screen;
            // no retry (mirrors RewardsVerificationScreen).
            navigate(AppRoute.REWARDS_ACCOUNT_NOT_FOUND);
            break;
          case 'invalid_lmg_selection':
            // REQ-3: server rejected the dept/state — should be unreachable via the
            // dropdowns, so log it as client drift, and prompt a reselect.
            logWarning(
              new Error('LMG signup rejected dept/state as invalid (dropdown drift)'),
              'LmgStaffSelectionScreen.invalid_lmg_selection',
            );
            setSignUpError('Please reselect your department and state, then try again.');
            break;
          default:
            // R21 + generic transport failures — inline error, no navigate-away.
            setSignUpError('Something went wrong. Please try again.');
            break;
        }
      } catch (_e) {
        if (!mountedRef.current) return;
        setSignUpError('Something went wrong. Please try again.');
      } finally {
        if (mountedRef.current) {
          setIsSubmitting(false);
        }
      }
    };

    return (
      <KeyboardAwareScrollView
        style={{flex: 1}}
        contentContainerStyle={{flex: 1}}
        keyboardShouldPersistTaps="always">
        <ContentView parentStyle={{flex: 1, paddingBottom: 16}} style={{flex: 1}}>
          <BackButton
            onPress={() => {
              navigation.navigate(AppRoute.REWARDS_SIGN_IN, rewardsStore.signInRouteParams);
            }}
          />
          <RewardsProgramImage variant="onLight" />

          <View>
            <Text variant="heading1">SELECT YOUR DEPARTMENT AND STATE</Text>
          </View>
          <Text>
            Select the department and state where you work to complete your staff registration.
          </Text>

          {bothSelected ? (
            <RewardsCard
              variant="subtle"
              padding="s4"
              style={{marginTop: 16}}
              testID="lmg-selected-info">
              <View style={{gap: 14}}>
                <SelectionSummaryRow
                  icon="briefcase"
                  label="Department"
                  value={selectedDepartment as string}
                  testID="lmg-selected-department"
                />
                <SelectionSummaryRow
                  icon="location-dot"
                  label="State"
                  value={selectedState as string}
                  testID="lmg-selected-state"
                />
              </View>
              <View
                style={{
                  borderTopWidth: 1,
                  borderTopColor: SUMMARY_DIVIDER,
                  marginTop: 16,
                  paddingTop: 12,
                }}>
                <RewardsLink
                  onPress={handleChangeSelection}
                  align="flex-start"
                  accessibilityLabel="Change selected department and state"
                  testID="lmg-change-selection">
                  Change Selection
                </RewardsLink>
              </View>
            </RewardsCard>
          ) : (
            <LmgDepartmentStatePicker
              departments={rewardsStore.lmgDepartments}
              states={rewardsStore.lmgStates}
              selectedDepartment={selectedDepartment}
              selectedState={selectedState}
              onSelectDepartment={handleSelectDepartment}
              onSelectState={handleSelectState}
              loading={rewardsStore.lmgOptionsLoading}
              error={rewardsStore.lmgOptionsError}
              onRetry={handleRetryOptions}
              testID="lmg-department-state-picker"
            />
          )}

          <View style={{gap: 8, marginTop: 16}}>
            <Button
              title="CONTINUE"
              onPress={handleContinue}
              disabled={!bothSelected || isSubmitting}
              loading={isSubmitting}
            />
            {signUpError ? (
              <Text
                variant="body2"
                style={{marginTop: 8, color: '#C81E1E'}}
                accessibilityRole="alert"
                accessibilityLiveRegion="polite"
                testID="lmg-signup-error">
                {signUpError}
              </Text>
            ) : null}
          </View>
        </ContentView>
      </KeyboardAwareScrollView>
    );
  },
);

export default LmgStaffSelectionScreen;
